import { useSelector } from 'react-redux';
import { Production } from '__types__';
import { DndList, gridListDropIndicator } from 'v5/patterns/dnd/DndList';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';
import {
  ProductionResourceRowCells,
  productionResourceParentGridColumns,
  productionResourceRowClassName
} from './ProductionResourceRow';
import type { ProductionStatus } from './productionHelpers';
import { ProductionResourceCount } from './useProductionResourceCounts';

export interface ProductionResourcesPreviewProps {
  /** Productions to display */
  productions: Production[];
  /** Resource count per production ID (independent status per production) */
  resourceCount: ProductionResourceCount;
  /** Optional empty state message */
  emptyMessage?: string;
  /** Optional section title */
  title?: string;
  /** Optional section description */
  description?: string;
  /** When set, productions can be reordered via drag and drop */
  onReorder?: (orderedProductionIds: number[]) => void;
}

/**
 * Reusable component for displaying production resources preview.
 * Shows production cards with color bar, icon, name, code, date, status badge, and resource count.
 */
export function ProductionResourcesPreview({
  productions,
  resourceCount,
  emptyMessage = 'No productions selected',
  title = 'Resources from productions',
  description = 'Resources booked on the selected date will be added into the callsheet.',
  onReorder
}: ProductionResourcesPreviewProps) {
  const statuses = useSelector(
    (state: any) => state?.statuses?.data || {}
  ) as Record<number, ProductionStatus>;

  const listStyle = {
    gridTemplateColumns:
      onReorder && productions.length > 1
        ? productionResourceParentGridColumns.sortable
        : productionResourceParentGridColumns.default
  };

  return (
    <div className="space-y-4 pt-6 border-t border-border-default flex flex-col min-h-0 flex-1">
      <div className="space-y-2 shrink-0">
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="text-sm text-fg-muted">{description}</p>
      </div>

      {/* The drop line overhangs the first row by more than `pt-3`; `-mt-1` + `pt-4` give it room without moving the rows. */}
      <ScrollShadowWrapper className="flex-1 min-h-0 -mt-1">
        <DndList
          items={productions}
          getKey={production => production.id}
          getTextValue={production =>
            production.title ?? `Production ${production.id}`
          }
          ariaLabel={title}
          onReorder={onReorder}
          emptyState={emptyMessage}
          className="grid gap-y-0 pt-4"
          style={listStyle}
          renderDropIndicator={gridListDropIndicator('3')}
          itemClassName={productionResourceRowClassName('mb-3')}
        >
          {(production, kit) => (
            <ProductionResourceRowCells
              production={production}
              statuses={statuses}
              resourceCount={resourceCount}
              dragHandle={kit.dragHandle}
            />
          )}
        </DndList>
      </ScrollShadowWrapper>
    </div>
  );
}
